import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Briefcase,
  Users,
  Target,
  ChevronRight,
  FileText,
} from "lucide-react";
import { useToast } from "../components/shared/ToastProvider";
import { useNavigation } from "../hooks/useInteractiveElement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-xl shadow-md p-6 card-hover cursor-pointer transform transition-all hover:scale-105"
    data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 font-medium">{title}</p>
        <h3
          className="text-3xl font-bold text-gray-900 mt-2"
          data-testid={`stat-value-${title.toLowerCase().replace(/\s/g, "-")}`}
        >
          {value}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`${color} rounded-full p-4`}>
        <Icon className="h-8 w-8 text-white" />
      </div>
    </div>
    <div className="mt-3 flex items-center text-sm text-blue-600 font-medium">
      <span>View details</span>
      <ChevronRight className="h-4 w-4 ml-1" />
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const navigateWithFeedback = useNavigation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentJobs, setRecentJobs] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, jobsResponse, openRequestsResponse] =
        await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/jobs`),
          axios.get(`${API}/open-requests`).catch(() => ({ data: [] })), // Graceful fallback if endpoint doesn't exist yet
        ]);

      setStats(statsResponse.data);
      setRecentJobs(jobsResponse.data.slice(0, 5));
      setOpenJobs(jobsResponse.data.filter((job) => job.status === "open"));
      setOpenRequests(openRequestsResponse.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pipelineStages = stats?.pipeline_by_stage || {};
  const stageOrder = ["sourced", "screening", "interview", "offer", "hired"];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome to Juno Talent Assist Platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Open Requests"
          value={openRequests?.length || 0}
          icon={FileText}
          color="bg-indigo-500"
          subtitle="From vendor portals"
          onClick={() =>
            navigateWithFeedback("/open-requests", "Loading open requests...")
          }
        />
        <StatCard
          title="Total Jobs"
          value={stats?.total_jobs || 0}
          icon={Briefcase}
          color="bg-blue-500"
          subtitle={`${stats?.open_jobs || 0} open positions`}
          onClick={() =>
            navigateWithFeedback("/jobs", "Loading job management...")
          }
        />
        <StatCard
          title="Total Candidates"
          value={stats?.total_candidates || 0}
          icon={Users}
          color="bg-green-500"
          subtitle="In database"
          onClick={() =>
            navigateWithFeedback("/candidates", "Loading candidates...")
          }
        />
        <StatCard
          title="Submissions"
          value={stats?.total_in_pipeline || 0}
          icon={Target}
          color="bg-purple-500"
          subtitle="Candidates in process"
          onClick={() =>
            navigateWithFeedback("/pipeline", "Loading candidate pipeline...")
          }
        />
        <StatCard
          title="Hire Rate"
          value={`${
            stats?.total_in_pipeline > 0
              ? Math.round(
                  ((pipelineStages.hired || 0) / stats.total_in_pipeline) * 100
                )
              : 0
          }%`}
          icon={TrendingUp}
          color="bg-orange-500"
          subtitle="Success rate"
          onClick={() =>
            navigateWithFeedback("/pipeline", "Loading pipeline analytics...")
          }
        />
      </div>

      {/* Pipeline Stages Breakdown */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            Submissions Overview
          </h2>
          <button
            onClick={() =>
              navigateWithFeedback("/pipeline", "Opening submissions view...")
            }
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
          >
            View All Submissions <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {stageOrder.map((stage) => {
            const stageColors = {
              sourced: "hover:bg-gray-100 hover:border-gray-400",
              screening: "hover:bg-blue-50 hover:border-blue-400",
              interview: "hover:bg-purple-50 hover:border-purple-400",
              offer: "hover:bg-yellow-50 hover:border-yellow-400",
              hired: "hover:bg-green-50 hover:border-green-400",
            };

            return (
              <div
                key={stage}
                onClick={() => {
                  const count = pipelineStages[stage] || 0;
                  if (openJobs.length > 0 && count > 0) {
                    showToast(`Viewing ${count} candidates in ${stage} stage`);
                    navigate(`/pipeline?job=${openJobs[0].id}&stage=${stage}`);
                  } else if (count > 0) {
                    showToast(`Viewing ${count} candidates in ${stage} stage`);
                    navigate("/pipeline");
                  } else {
                    showToast(`No candidates in ${stage} stage`, "info");
                  }
                }}
                className={`text-center p-4 bg-gray-50 rounded-lg border-2 border-transparent cursor-pointer transform transition-all ${
                  stageColors[stage]
                } ${
                  (pipelineStages[stage] || 0) > 0
                    ? "hover:scale-105"
                    : "opacity-60"
                }`}
                data-testid={`pipeline-stage-card-${stage}`}
              >
                <p className="text-sm text-gray-600 capitalize mb-2">{stage}</p>
                <p
                  className="text-2xl font-bold text-gray-900"
                  data-testid={`pipeline-stage-${stage}`}
                >
                  {pipelineStages[stage] || 0}
                </p>
                {(pipelineStages[stage] || 0) > 0 && (
                  <p className="text-xs text-blue-600 mt-2 font-medium flex items-center justify-center">
                    View <ChevronRight className="h-3 w-3" />
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            Recent Job Postings
          </h2>
          <a
            href="/jobs"
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            View all →
          </a>
        </div>

        {recentJobs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No jobs posted yet</p>
            <a
              href="/jobs"
              className="text-blue-600 hover:text-blue-700 mt-2 inline-block"
            >
              Create your first job
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {recentJobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                data-testid={`recent-job-${job.id}`}
              >
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{job.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {job.department} • {job.location} • {job.experience_level}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      job.status === "open"
                        ? "bg-green-100 text-green-800"
                        : job.status === "draft"
                        ? "bg-gray-100 text-gray-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {job.status}
                  </span>
                  <button
                    onClick={() =>
                      navigateWithFeedback(
                        `/pipeline?job=${job.id}`,
                        `Loading pipeline for ${job.title}...`
                      )
                    }
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    View Submissions →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() =>
            navigateWithFeedback("/jobs", "Opening job management...")
          }
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 hover:from-blue-600 hover:to-blue-700 shadow-lg card-hover text-left w-full"
          data-testid="quick-action-create-job"
        >
          <Briefcase className="h-8 w-8 mb-3" />
          <h3 className="text-lg font-bold mb-2">Create New Job</h3>
          <p className="text-sm text-blue-100">
            Post a new job requisition with AI-powered parsing
          </p>
        </button>

        <button
          onClick={() =>
            navigateWithFeedback("/pipeline", "Loading submissions...")
          }
          className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 hover:from-purple-600 hover:to-purple-700 shadow-lg card-hover text-left w-full"
          data-testid="quick-action-view-submissions"
        >
          <Target className="h-8 w-8 mb-3" />
          <h3 className="text-lg font-bold mb-2">View Submissions</h3>
          <p className="text-sm text-purple-100">
            Track candidate submissions and progress
          </p>
        </button>

        <button
          onClick={() =>
            navigateWithFeedback("/ai-sourcing", "Opening AI sourcing...")
          }
          className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 hover:from-green-600 hover:to-green-700 shadow-lg card-hover text-left w-full"
          data-testid="quick-action-source-candidates"
        >
          <Users className="h-8 w-8 mb-3" />
          <h3 className="text-lg font-bold mb-2">Source Candidates</h3>
          <p className="text-sm text-green-100">
            Find candidates from LinkedIn, Indeed & Monster
          </p>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
